import React, { useEffect, useRef, useState } from 'react';
import { FaArrowRight } from 'react-icons/fa';
import { trackMenuView, trackSectionView, trackButtonClick } from '../utils/gtm';
import { safeBase64Encode } from '../utils/safeEncode';
import ExploreModal from './ExploreModal';
import { exploreData } from '../data/exploreData';
import './MenuShowcase.css';

const cards = [
  {
    tag: 'Cooked Meals',
    title: 'South Indian & North Indian',
    bullets: ['Dosa, Idli, Vada', 'Thalis & Combos', 'Snacks & Specials'],
    img: 'https://cdn.pixabay.com/photo/2017/09/16/19/21/salad-2756467_1280.jpg',
  },
  {
    tag: 'Fresh Vegetables',
    title: 'Farm‑Fresh Daily',
    bullets: ['Hand‑picked', 'Cleaned & Ready', 'Seasonal Selection'],
    img: 'https://cdn.pixabay.com/photo/2017/05/11/19/44/fresh-fruits-2305192_1280.jpg',
  },
  {
    tag: 'Beverages & Add‑ons',
    title: 'Coffee, Juices, Chutneys',
    bullets: [
      'Filter coffee, buttermilk',
      'Fresh juices',
      'Chutneys & Pickles',
    ],
    img: 'https://images.unsplash.com/photo-1546548970-71785318a17b?ixlib=rb-4.0.3&auto=format&fit=crop&w=1280&q=80',
  },
  {
    tag: 'Custom Combos',
    title: 'Solo, Family, Office',
    bullets: ['Meal packs', 'Party & Bulk', 'Office lunches'],
    img: 'https://cdn.pixabay.com/photo/2014/10/19/20/59/hamburger-494706_1280.jpg',
  },
];

const MenuShowcase = () => {
  const ref = useRef(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);

  useEffect(() => {
    const io = new IntersectionObserver(
      (e) =>
        e.forEach((x) => {
          if (x.isIntersecting) {
            // GTM: Track menu section view
            trackSectionView('Menu');
            x.target.classList.add('show');
          }
        }),
      { threshold: 0.2 }
    );
    ref.current?.querySelectorAll('.reveal').forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  // GTM: Track menu exploration
  const handleExplore = (category) => {
    trackMenuView(category);
    trackButtonClick('Explore', `Menu - ${category}`);
    setSelectedCategory(category);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedCategory(null);
  };

  const handleImageError = (e, fallbackText) => {
    if (e.target.dataset.fallback === 'true') {
      const parent = e.target.parentElement;
      parent.innerHTML = `
        <div class="fallback-image">
          ${fallbackText}
        </div>
      `;
      return;
    }

    e.target.dataset.fallback = 'true';
    
    // Create SVG content with safe encoding
    const svgContent = `
      <svg width="400" height="240" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <linearGradient id="grad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" style="stop-color:#FF7A3D;stop-opacity:1" />
            <stop offset="100%" style="stop-color:#FFB14B;stop-opacity:1" />
          </linearGradient>
        </defs>
        <rect width="100%" height="100%" fill="url(#grad)"/>
        <text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" 
              fill="white" font-size="18" font-weight="bold" font-family="Sora, sans-serif">
          ${fallbackText}
        </text>
      </svg>
    `;
    
    const encodedSvg = safeBase64Encode(svgContent);
    if (encodedSvg) {
      e.target.src = `data:image/svg+xml;base64,${encodedSvg}`;
    } else {
      // Ultimate fallback - use URL encoding
      e.target.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgContent)}`;
    }
  };

  return (
    <>
      <section id="menu" className="menu-showcase">
        <div className="menu-container" ref={ref}>
          <div className="menu-header reveal">
            <h2 className="menu-title">What You Can Order</h2>
            <p className="menu-subtitle">
              Explore fresh meals, veggies, beverages and build custom combos
              for solo diners, families and offices.
            </p>
          </div>
          
          <div className="menu-cards-grid">
            {cards.map((c, i) => (
              <article key={i} className="menu-card reveal">
                <div className="menu-card-image">
                  <img
                    src={c.img}
                    alt={c.tag}
                    onError={(e) => handleImageError(e, c.tag)}
                  />
                  <div className="menu-card-overlay" />
                  <div className="menu-card-badge">{c.tag}</div>
                </div>
                
                <div className="menu-card-content">
                  <h3 className="menu-card-title">{c.title}</h3>
                  <ul className="menu-card-bullets">
                    {c.bullets.map((b, bi) => (
                      <li key={bi}>{b}</li>
                    ))}
                  </ul>
                  {/* <div className="menu-card-action">
                    <button
                      className="btn btn-ghost explore-btn"
                      onClick={() => handleExplore(c.tag)}
                    >
                      <FaArrowRight /> Explore
                    </button>
                  </div> */}
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <ExploreModal
        isOpen={isModalOpen}
        onClose={closeModal}
        category={selectedCategory}
        data={selectedCategory ? exploreData[selectedCategory] : null}
      />
    </>
  );
};

export default MenuShowcase;
